import { shallowRef, triggerRef } from 'vue'

const STORAGE_KEY = 'adx-email-testing-read'
const MAX_IDS = 500

function load(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return new Set(Array.isArray(parsed) ? parsed : [])
  } catch {
    return new Set()
  }
}

// shallowRef + manual triggerRef avoids deep-reactivity tracking on a mutable
// Set, which would otherwise pay a proxy cost on every read. Per ADX
// vue-best-practices.md for mutable collections.
const readIds = shallowRef<Set<string>>(load())

function persist(): void {
  try {
    const arr = Array.from(readIds.value).slice(-MAX_IDS)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr))
  } catch {
    // Quota exceeded or storage disabled — degrade silently. The worst case
    // is that every message looks unread on next page load.
  }
}

/**
 * Tracks which inbox messages the user has opened, persisted across tabs
 * and reloads in localStorage. Bounded to the most recent MAX_IDS so the
 * store doesn't grow unbounded as messages age out of the sink.
 */
export function useInboxReadState() {
  function isRead(id: string): boolean {
    return readIds.value.has(id)
  }

  function markRead(id: string): void {
    if (readIds.value.has(id)) return
    readIds.value.add(id)
    triggerRef(readIds)
    persist()
  }

  function markUnread(id: string): void {
    if (!readIds.value.has(id)) return
    readIds.value.delete(id)
    triggerRef(readIds)
    persist()
  }

  function markAllRead(ids: string[]): void {
    let changed = false
    for (const id of ids) {
      if (!readIds.value.has(id)) {
        readIds.value.add(id)
        changed = true
      }
    }
    if (changed) {
      triggerRef(readIds)
      persist()
    }
  }

  return { isRead, markRead, markUnread, markAllRead, readIds }
}
